import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, SignOut, Plus, Trash, TrendUp, Truck, CheckCircle, ClockCounterClockwise, Envelope, X, UploadSimple, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SHIP_STATUSES = ["Booked", "Picked Up", "In Transit", "Customs Cleared", "Out for Delivery", "Delivered"];
const BOOKING_STATUSES = ["Pending", "Confirmed", "Picked Up", "Cancelled"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, sh, bk, ct] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/shipments"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data); setShipments(sh.data); setBookings(bk.data); setContacts(ct.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doLogout = () => { logout(); navigate("/login"); };

  const updateShipStatus = async (tn, status) => {
    try {
      await api.put(`/admin/shipments/${tn}`, { status, description: `Status updated to ${status}` });
      toast.success(`${tn} → ${status}`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteShip = async (tn) => {
    try { await api.delete(`/admin/shipments/${tn}`); toast.success(`Deleted ${tn}`); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadPod = async (tn, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/shipments/${tn}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`POD uploaded for ${tn}`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateBooking = async (id, status) => {
    try { await api.put(`/admin/bookings/${id}`, { status }); toast.success(`Booking → ${status}`); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const statCards = stats ? [
    { label: "Total Shipments", value: stats.total_shipments, icon: Package },
    { label: "In Transit", value: stats.in_transit, icon: Truck },
    { label: "Delivered", value: stats.delivered, icon: CheckCircle },
    { label: "Pending Pickups", value: stats.pending_bookings, icon: ClockCounterClockwise },
    { label: "Total Bookings", value: stats.total_bookings, icon: TrendUp },
    { label: "Messages", value: stats.contacts, icon: Envelope },
  ] : [];

  const tabs = [
    ["overview", "Overview"], ["shipments", `Shipments (${shipments.length})`],
    ["bookings", `Bookings (${bookings.length})`], ["contacts", `Messages (${contacts.length})`],
  ];

  return (
    <div className="min-h-screen bg-obsidian text-stark font-sans">
      <header className="border-b border-neutral-800 sticky top-0 bg-obsidian/90 backdrop-blur z-30">
        <div className="max-w-[1500px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-signal text-white p-1.5"><Package weight="bold" size={20} /></span>
            <span className="font-display font-semibold text-lg tracking-tight">MAMA PARCEL</span>
            <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 border border-neutral-700 px-2 py-0.5 ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-neutral-400 hidden sm:block">{user?.email}</span>
            <button data-testid="logout-btn" onClick={doLogout}
              className="flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-signal transition-colors">
              <SignOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 py-8">
        <div className="flex gap-1 border-b border-neutral-800 mb-8 overflow-x-auto">
          {tabs.map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-3 font-medium text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                tab === k ? "border-signal text-signal" : "border-transparent text-neutral-400 hover:text-stark"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800" data-testid="stats-grid">
            {statCards.map((c) => (
              <div key={c.label} className="bg-neutral-900 p-6">
                <c.icon weight="duotone" size={28} className="text-signal" />
                <p className="font-display font-semibold text-5xl mt-4 tracking-tighter">{c.value}</p>
                <p className="font-mono text-[11px] tracking-widest uppercase text-neutral-400 mt-1">{c.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "shipments" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display font-medium text-2xl tracking-tight">Shipments</h2>
              <button data-testid="new-shipment-btn" onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 bg-signal text-white font-medium px-4 py-2 text-sm hover:bg-signal-hover transition-colors">
                <Plus weight="bold" /> New Shipment
              </button>
            </div>
            <div className="border border-neutral-800 overflow-x-auto">
              <table className="w-full text-sm" data-testid="shipments-table">
                <thead className="bg-neutral-900 text-neutral-400 font-mono text-[11px] tracking-widest uppercase">
                  <tr>
                    {["Tracking", "Route", "Service", "Status", "Actions"].map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s) => (
                    <tr key={s.tracking_number} className="border-t border-neutral-800 hover:bg-neutral-900/50">
                      <td className="px-4 py-3 font-mono text-signal">{s.tracking_number}</td>
                      <td className="px-4 py-3">{s.origin} → {s.destination}</td>
                      <td className="px-4 py-3 text-neutral-400">{s.service_type} · {s.weight_kg}kg</td>
                      <td className="px-4 py-3">
                        <select data-testid={`ship-status-${s.tracking_number}`} value={s.status}
                          onChange={(e) => updateShipStatus(s.tracking_number, e.target.value)}
                          className="bg-neutral-800 border border-neutral-700 px-2 py-1 text-xs focus:border-signal outline-none">
                          {SHIP_STATUSES.map((st) => <option key={st}>{st}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <label data-testid={`pod-upload-${s.tracking_number}`}
                            className="flex items-center gap-1 text-neutral-400 hover:text-signal transition-colors cursor-pointer"
                            title="Upload proof of delivery">
                            <UploadSimple size={18} />
                            {s.pod_images?.length > 0 && (
                              <span className="flex items-center gap-0.5 text-signal text-xs font-mono">
                                <ImageIcon size={14} weight="fill" />{s.pod_images.length}
                              </span>
                            )}
                            <input type="file" accept="image/*" className="hidden"
                              onChange={(e) => { uploadPod(s.tracking_number, e.target.files[0]); e.target.value = ""; }} />
                          </label>
                          <button data-testid={`del-ship-${s.tracking_number}`} onClick={() => deleteShip(s.tracking_number)}
                            className="text-neutral-500 hover:text-red-400 transition-colors"><Trash size={18} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shipments.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">No shipments yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="border border-neutral-800 overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead className="bg-neutral-900 text-neutral-400 font-mono text-[11px] tracking-widest uppercase">
                <tr>{["Ref", "Customer", "Route", "Service", "Status"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}</tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-neutral-800 hover:bg-neutral-900/50">
                    <td className="px-4 py-3 font-mono text-signal">{b.reference}</td>
                    <td className="px-4 py-3">{b.name}<br /><span className="text-neutral-500 text-xs">{b.phone}</span></td>
                    <td className="px-4 py-3 text-neutral-400 text-xs max-w-xs">{b.pickup_address} → {b.delivery_address}</td>
                    <td className="px-4 py-3 text-neutral-400">{b.service_type} · {b.weight_kg}kg</td>
                    <td className="px-4 py-3">
                      <select data-testid={`booking-status-${b.id}`} value={b.status} onChange={(e) => updateBooking(b.id, e.target.value)}
                        className="bg-neutral-800 border border-neutral-700 px-2 py-1 text-xs focus:border-signal outline-none">
                        {BOOKING_STATUSES.map((st) => <option key={st}>{st}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="contacts-list">
            {contacts.map((c) => (
              <div key={c.id} className="border border-neutral-800 p-5 bg-neutral-900">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-stark">{c.name}</p>
                    <p className="font-mono text-xs text-signal">{c.email}</p>
                  </div>
                  <span className="font-mono text-[10px] text-neutral-400">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-neutral-300 text-sm mt-3 leading-relaxed">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-neutral-500 col-span-2 text-center py-10">No messages yet.</p>}
          </div>
        )}
      </div>

      {showCreate && <CreateShipmentModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadAll(); }} />}
    </div>
  );
}

function CreateShipmentModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ sender_name: "", receiver_name: "", origin: "", destination: "", service_type: "Domestic", weight_kg: 1, status: "Booked", eta: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-neutral-800 border border-neutral-700 focus:border-signal outline-none px-3 py-2 text-sm transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/shipments", { ...form, weight_kg: parseFloat(form.weight_kg) || 1, eta: form.eta || null });
      toast.success(`Created ${data.tracking_number}`);
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-neutral-900 border-2 border-neutral-700 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="create-shipment-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display font-medium text-xl tracking-tight">New Shipment</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-stark"><X size={22} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Sender</label>
            <input data-testid="cs-sender" required className={inputCls} value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Receiver</label>
            <input data-testid="cs-receiver" required className={inputCls} value={form.receiver_name} onChange={(e) => set("receiver_name", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Origin</label>
            <input data-testid="cs-origin" required className={inputCls} value={form.origin} onChange={(e) => set("origin", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Destination</label>
            <input data-testid="cs-destination" required className={inputCls} value={form.destination} onChange={(e) => set("destination", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Service</label>
            <select data-testid="cs-service" className={inputCls} value={form.service_type} onChange={(e) => set("service_type", e.target.value)}>
              <option>Domestic</option><option>International</option></select></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Weight (kg)</label>
            <input data-testid="cs-weight" type="number" min="0.1" step="0.1" className={inputCls} value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">ETA</label>
            <input data-testid="cs-eta" className={inputCls} placeholder="2 days" value={form.eta} onChange={(e) => set("eta", e.target.value)} /></div>
          <div><label className="font-mono text-[10px] tracking-widest uppercase text-neutral-400">Status</label>
            <select data-testid="cs-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
              {SHIP_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <button data-testid="cs-submit" disabled={loading} className="col-span-2 bg-signal text-white font-medium py-3 hover:bg-signal-hover transition-colors disabled:opacity-60 mt-2">
            {loading ? "Creating..." : "Create Shipment"}
          </button>
        </form>
      </div>
    </div>
  );
}
